import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { type AgentThreadToolResultContent } from "@/shared/domain/agentThread";
import {
  displayToolName,
  isClientToolName,
} from "@/shared/domain/agentThread/clientTools/ClientTools";
import { describeToolCall } from "@/shared/domain/agentThread/describeToolCall";
import {
  BuildIcon,
  ChevronRightIcon,
  ExpandMoreIcon,
  LaptopMacIcon,
} from "@/shared/foundation/icons/mui";

import { toolResultStatus } from "../liveToolCalls";

import { ArtifactStrip } from "./ArtifactStrip";
import { resolveArtifacts } from "./outputsField";
import { RunPythonPillLabel } from "./RunPythonPillLabel";
import { ToolCallStatusBadge } from "./ToolCallStatusBadge";
import { ToolDetailPanel } from "./ToolDetailPanel";
import styles from "./ToolUseGroup.module.css";

const MS_TO_SECONDS_THRESHOLD = 100;

function formatRuntime(runtimeMs: number): string {
  if (runtimeMs < MS_TO_SECONDS_THRESHOLD) {
    return `${runtimeMs}ms`;
  }

  return `${(runtimeMs / 1000).toFixed(2)}s`;
}

interface ToolUseGroupProps {
  threadId: string;
  toolResults: AgentThreadToolResultContent[];
  /** Each tool_use_id's proposed input, used to label the generic tools. */
  toolInputs?: Record<string, Record<string, unknown>>;
}

export function ToolUseGroup({
  threadId,
  toolResults,
  toolInputs,
}: ToolUseGroupProps): React.JSX.Element | null {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [selectedToolUseId, setSelectedToolUseId] = React.useState<
    string | undefined
  >();

  if (toolResults.length === 0) {
    return null;
  }

  const totalRuntimeMs = toolResults.reduce(
    (total, toolResult) => total + toolResult.runtime_ms,
    0,
  );
  // A collapsed group hides its rows' error badges, so the summary counts them.
  const failedCount = toolResults.filter(
    (toolResult) => toolResultStatus(toolResult) === "error",
  ).length;
  const summaryLabel = `Used ${toolResults.length} tool${toolResults.length === 1 ? "" : "s"} (${formatRuntime(totalRuntimeMs)})${failedCount > 0 ? `, ${failedCount} failed` : ""}`;

  const handleGroupToggle = () => {
    if (isExpanded) {
      setSelectedToolUseId(undefined);
    }

    setIsExpanded((currentValue) => !currentValue);
  };

  const handleToolClick = (toolUseId: string) => {
    setSelectedToolUseId((currentValue) => {
      if (currentValue === toolUseId) {
        return undefined;
      }

      return toolUseId;
    });
  };

  const renderToolResult = (toolResult: AgentThreadToolResultContent) => {
    const isSelected = selectedToolUseId === toolResult.tool_use_id;
    const isClient = isClientToolName(toolResult.tool_name);
    const Icon = isClient ? LaptopMacIcon : BuildIcon;
    // A grouped call gets the same specific label as an ungrouped one:
    // "list_dataset_events" rather than a row of identical "list_related"s.
    const displayName = describeToolCall(
      displayToolName(toolResult.tool_name),
      toolInputs?.[toolResult.tool_use_id],
    );
    // Rendered outside `isSelected`, so files show without opening the detail panel.
    const artifacts = resolveArtifacts(toolResult);
    const isRunPython = toolResult.tool_name === "run_python";

    return (
      <div key={toolResult.tool_use_id} className={styles.toolItem}>
        <button
          type="button"
          className={styles.toolButton}
          onClick={() => handleToolClick(toolResult.tool_use_id)}
          aria-expanded={isSelected}
          aria-label={`Toggle tool details for ${displayName}`}
        >
          <span className={styles.buttonContent}>
            <span className={styles.iconAndText}>
              <Icon
                className={isClient ? styles.clientToolIcon : styles.buildIcon}
              />
              <RobotoTypography component="span" className={styles.text}>
                {isRunPython ? (
                  <RunPythonPillLabel
                    toolResult={toolResult}
                    toolInput={toolInputs?.[toolResult.tool_use_id]}
                  />
                ) : (
                  <>
                    Used {isClient ? "Client " : ""}Tool <b>{displayName}</b> (
                    {formatRuntime(toolResult.runtime_ms)})
                  </>
                )}
              </RobotoTypography>
              {/* A finished call reads as done from its "Used" wording; only a failure needs a
                  badge. run_python's label carries its own status. */}
              {!isRunPython && toolResultStatus(toolResult) === "error" && (
                <ToolCallStatusBadge status="error" />
              )}
            </span>
            {isSelected ? (
              <ExpandMoreIcon className={styles.chevronIcon} />
            ) : (
              <ChevronRightIcon className={styles.chevronIcon} />
            )}
          </span>
        </button>

        {/* An open run_python card lists the files itself, under its Files heading. */}
        {artifacts && !(isRunPython && isSelected) && (
          <ArtifactStrip
            threadId={threadId}
            toolUseId={toolResult.tool_use_id}
            artifacts={artifacts}
          />
        )}

        {isSelected && (
          <div className={styles.detailPanelContainer}>
            <ToolDetailPanel
              threadId={threadId}
              toolUseId={toolResult.tool_use_id}
            />
          </div>
        )}
      </div>
    );
  };

  if (toolResults.length === 1) {
    return (
      <div className={styles.indentedContainer}>
        <div className={styles.groupContainer}>
          {renderToolResult(toolResults[0])}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.indentedContainer}>
      <div className={styles.groupContainer}>
        <button
          type="button"
          className={styles.summaryButton}
          onClick={handleGroupToggle}
          aria-expanded={isExpanded}
          aria-label="Toggle grouped tool results"
        >
          <span className={styles.buttonContent}>
            <span className={styles.iconAndText}>
              <BuildIcon className={styles.buildIcon} />
              <RobotoTypography component="span" className={styles.text}>
                {summaryLabel}
              </RobotoTypography>
            </span>
            {isExpanded ? (
              <ExpandMoreIcon className={styles.chevronIcon} />
            ) : (
              <ChevronRightIcon className={styles.chevronIcon} />
            )}
          </span>
        </button>

        {isExpanded && (
          <div className={styles.toolList}>
            {toolResults.map(renderToolResult)}
          </div>
        )}
      </div>
    </div>
  );
}
